import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Progress } from "../ui/progress";
import { Button } from "../ui/button";
import { Calendar, Clock, CheckCircle, XCircle, AlertCircle, FileText, Loader2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../ui/dialog";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { toast } from "sonner";
import API from "../api/api01";

export function Attendance() {
  const [selectedCourse, setSelectedCourse] = useState("all");
  const [leaveReason, setLeaveReason] = useState("");
  const [loading, setLoading] = useState(true);

  const [timetable, setTimetable] = useState<any[]>([]);
  const [courseAttendance, setCourseAttendance] = useState<any[]>([]);
  const [leaveRequests, setLeaveRequests] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const studentId = user.id;

  useEffect(() => {
    if (studentId) {
      fetchData();
    }
  }, [studentId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [timetableRes, summaryRes, leavesRes] = await Promise.all([
        API.get(`/superadmin/student/timetable/${studentId}`),
        API.get(`/superadmin/student/attendance-summary/${studentId}`),
        API.get(`/superadmin/student/leave-requests/${studentId}`)
      ]);
      setTimetable(timetableRes.data);
      setSummary(summaryRes.data);
      setCourseAttendance(summaryRes.data.course_wise);
      setLeaveRequests(leavesRes.data);
    } catch (error) {
      console.error("Error fetching attendance data:", error);
      toast.error("Failed to load attendance data");
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveRequest = async () => {
    if (!leaveReason.trim()) {
      toast.error("Please provide a reason for leave");
      return;
    }

    try {
      await API.post(`/superadmin/student/leave-requests/${studentId}`, {
        date: new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
        reason: leaveReason
      });
      toast.success("Leave request submitted successfully!");
      setLeaveReason("");
      fetchData(); 
    } catch (error) {
      toast.error("Failed to submit leave request");
    }
  };

  // --- Logic for Color Thresholds ---
  const getAttendanceColor = (percentage: number) => {
    if (percentage >= 75) return "text-emerald-600 dark:text-emerald-400"; // Green
    if (percentage >= 60) return "text-amber-500 dark:text-amber-400";   // Yellow
    return "text-red-600 dark:text-red-400";                             // Red
  };

  const getProgressClass = (percentage: number) => {
    if (percentage >= 75) return "[&>div]:bg-emerald-500"; // Green
    if (percentage >= 60) return "[&>div]:bg-amber-500";   // Yellow
    return "[&>div]:bg-red-500";                           // Red
  };

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Attendance</h1>
          <p className="text-muted-foreground text-sm mt-1">Track your attendance and manage leave requests</p>
        </div>
        <Dialog>
          <DialogTrigger asChild>
            <Button className="gap-2 text-white dark:text-foreground">
              <FileText className="h-4 w-4" />
              Request Leave
            </Button>
          </DialogTrigger>

          <DialogContent className="bg-white dark:bg-zinc-900 border border-border shadow-2xl">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold">
                Submit Leave Request
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="reason">Reason for Leave</Label>
                <Textarea
                  id="reason"
                  placeholder="Please provide a detailed reason for your leave request..."
                  value={leaveReason}
                  onChange={(e) => setLeaveReason(e.target.value)}
                  rows={4}
                  className="bg-white dark:bg-zinc-800"
                />
              </div>
              <p className="text-sm text-muted-foreground">
                Note: Leave requests will be reviewed by the admin and approved if valid.
              </p>
            </div>

            <DialogFooter>
              <Button
                onClick={handleLeaveRequest}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Submit Request
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="timetable">Timetable</TabsTrigger>
          <TabsTrigger value="leaves">Leave Requests</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <>
              {/* Overall Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-white dark:bg-zinc-900 border-border">
                  <CardContent className="pt-6">
                    <div className="text-center space-y-2">
                      <p className="text-sm text-muted-foreground">Overall Attendance</p>
                      <p className={`text-3xl font-bold ${getAttendanceColor(summary?.overall_percentage || 0)}`}>
                        {summary?.overall_percentage}%
                      </p>
                      <Progress 
                        value={summary?.overall_percentage} 
                        className={`h-2.5 bg-zinc-100 dark:bg-zinc-800 ${getProgressClass(summary?.overall_percentage || 0)}`} 
                      />
                      <p className="text-[10px] text-muted-foreground mt-1 text-left">Required minimum: 75%</p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center space-y-2">
                      <p className="text-sm text-muted-foreground">Classes Attended</p>
                      <p className="text-3xl font-semibold">{summary?.attended_count}/{summary?.total_count}</p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center space-y-2">
                      <p className="text-sm text-muted-foreground">On Leave</p>
                      <p className="text-3xl font-semibold text-amber-600">{summary?.leave_count}</p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center space-y-2">
                      <p className="text-sm text-muted-foreground">This Week</p>
                      <p className="text-3xl font-semibold">{summary?.this_week_count}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Course-wise Attendance */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Course-wise Attendance</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {courseAttendance.map((course, index) => (
                    <div key={index} className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-semibold">{course.course}</p>
                          <p className="text-xs text-muted-foreground">
                            {course.attended} Present • {course.leave} Leave • {course.total - course.attended - course.leave} Absent
                          </p>
                        </div>
                        <span className={`text-lg font-bold ${getAttendanceColor(course.percentage)}`}>
                          {course.percentage}%
                        </span>
                      </div>
                      <Progress
                        value={course.percentage}
                        className={`h-2 bg-zinc-100 dark:bg-zinc-800 ${getProgressClass(course.percentage)}`}
                      />
                    </div>
                  ))}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="timetable" className="space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Weekly Timetable & Attendance</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {timetable.length === 0 && (
                  <div className="text-center py-10 text-muted-foreground">
                    No timetable found for your department
                  </div>
                )}
                {timetable.map((day: any, dayIndex: number) => (
                  <div key={dayIndex} className="space-y-3">
                    <h4 className="text-sm font-semibold flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-primary" />
                      {day.day}
                    </h4>
                    <div className="space-y-2 pl-6">
                      {day.slots.map((slot: any, slotIndex: number) => (
                        <div
                          key={slotIndex}
                          className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted/30 transition-colors"
                        >
                          <div className="flex items-center gap-2 min-w-[100px]">
                            <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">{slot.time}</span>
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium">{slot.course}</p>
                            <p className="text-xs text-muted-foreground">{slot.room}</p>
                          </div>
                          <Badge
                            className={
                              slot.status === "present"
                                ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-200"
                                : slot.status === "leave"
                                  ? "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 hover:bg-amber-200"
                                  : "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 hover:bg-red-200"
                            }
                          >
                            {slot.status === "present" ? (
                              <><CheckCircle className="h-3 w-3 mr-1" /> Present</>
                            ) : slot.status === "leave" ? (
                              <><AlertCircle className="h-3 w-3 mr-1" /> Leave</>
                            ) : (
                              <><XCircle className="h-3 w-3 mr-1" /> Absent</>
                            )}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="leaves" className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Leave Requests</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {leaveRequests.length === 0 && (
                    <div className="text-center py-10 text-muted-foreground">
                      No leave requests found
                    </div>
                  )}
                  {leaveRequests.map((request: any) => (
                    <div
                      key={request.id}
                      className="flex items-start justify-between p-4 rounded-lg border border-border hover:bg-muted/30 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm font-medium">{request.date}</span>
                        </div>
                        <p className="text-sm text-muted-foreground">{request.reason}</p>
                      </div>
                      <Badge
                        className={
                          request.status === "Approved"
                            ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400"
                            : request.status === "Rejected"
                              ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
                              : "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400"
                        }
                      >
                        {request.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}